import React, { useMemo } from 'react';

import { LinearProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import duration, { DurationUnitsObjectType } from 'dayjs/plugin/duration';

dayjs.extend(duration);

/**
 * Props for the TimeProgressCell component.
 */
interface TimeProgressCellProps {
  /** Completed duration, e.g., { seconds: 500 }. Defaults to { minutes: 0 }. */
  timeCompleted?: DurationUnitsObjectType | null;

  /** Required duration, e.g., { hours: 1 }. Defaults to { minutes: 0 }. */
  timeRequired?: DurationUnitsObjectType | null;

  /** Optional override for status colors. */
  colors?: {
    /** Color for not started (0% progress). */
    notStarted?: string;
    /** Color for in-progress (0-100% progress). */
    inProgress?: string;
    /** Color for completed (100% progress). */
    completed?: string;
  };

  /** Text to display when both completed and required durations are 0. Defaults to 'Not Required'. */
  emptyLabel?: string;

  /** Whether to show the progress bar. Defaults to false. */
  showProgressBar?: boolean;
}

/**
 * Default colors for different progress states.
 */
const DEFAULT_COLORS = {
  notStarted: 'rgb(255, 59, 48)', // Red for 0% progress
  inProgress: 'rgb(255, 149, 0)', // Orange for 0-100% progress
  completed: 'rgb(52, 199, 89)', // Green for 100% progress
};

/**
 * A memoized component that renders a cell displaying time progress for MUI DataGrid.
 * Shows a text label with completed vs. required time in hours and an optional progress bar.
 * The text color reflects the progress state (not started, in progress, completed).
 *
 * @param props - The component props.
 * @returns A React element displaying the time progress.
 */
export const TimeProgressCell = React.memo(function TimeProgressCell({
  timeCompleted,
  timeRequired,
  colors = DEFAULT_COLORS,
  emptyLabel = 'Not Required',
  showProgressBar = false,
}: TimeProgressCellProps) {
  /**
   * Memoized computation of label, color, and progress percentage.
   * @returns An object containing the display label, color, and progress percentage.
   */
  const { label, color, progress } = useMemo(() => {
    const completedDuration = dayjs.duration(timeCompleted ?? { minutes: 0 });
    const requiredDuration = dayjs.duration(timeRequired ?? { minutes: 0 });
    const completedMs = completedDuration.asMilliseconds();
    const requiredMs = requiredDuration.asMilliseconds();

    // Empty case: both durations are 0
    if (completedMs === 0 && requiredMs === 0) {
      return { label: emptyLabel, color: 'text.primary', progress: 0 };
    }

    // Case: required duration is 0, show completed time only
    if (requiredMs === 0) {
      const completedHours = completedDuration.asHours().toFixed(2);
      return {
        label: `${completedHours} Hours`,
        color: colors.completed ?? DEFAULT_COLORS.completed,
        progress: 100, // Treat as complete for visualization
      };
    }

    // Normal case: calculate progress and format label
    const progress = Math.min((completedMs / requiredMs) * 100, 100);
    const completedHours = completedDuration.asHours().toFixed(2);
    const requiredHours = requiredDuration.asHours().toFixed(2);
    const label = `${completedHours} / ${requiredHours} Hours`;
    const color = (() => {
      if (progress === 0) return colors.notStarted ?? DEFAULT_COLORS.notStarted;
      if (progress === 100) return colors.completed ?? DEFAULT_COLORS.completed;
      return colors.inProgress ?? DEFAULT_COLORS.inProgress;
    })();

    return { label, color, progress };
  }, [timeCompleted, timeRequired, emptyLabel, colors]);

  return (
    <Stack justifyContent="center" height="100%" width="100%" gap={0.5}>
      <Typography variant="body2" color={color}>
        {label}
      </Typography>
      {showProgressBar && progress > 0 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
            },
          }}
        />
      )}
    </Stack>
  );
});

export default TimeProgressCell;
